import React, { useMemo, useState, useRef, useEffect } from "react";
import styles from "./ChatWindow.module.scss";
import ChatHeader from "./ChatHeader";
import MessageList, { MessageListRef } from "./MessageList";
import MessageInput from "./MessageInput";
import ChatHomepage from "./ChatHomepage";
import { useSearchParamsV6 } from "../../hooks/useSearchParamsV6";
import { useGetInfoRoom } from "../../hooks/useGetInfoRoom";
import { useJoinAChatRoom, useCheckListUserInRoomAdmin } from "../../hooks/queries/chat";
import { useClient } from "../../hooks/queries/useClient";
import { useRealtimeMessage } from "../../hooks/useRealtimeMessage";
import { ReplyState } from "../../utils/replyUtils";
import { RoomInfo } from "../../types/chat";
import { Spin } from "antd";
import { ChatClient } from "@azure/communication-chat";
import { PinnedMessages } from "../../components/PinnedMessages";
import { useInfoMine } from "../../hooks/queries/user";

interface ChatWindowProps {
  onToggleRightPanel: () => void;
  onOpenSearch: () => void;
  onBack?: () => void;
}

const ChatWindow: React.FC<ChatWindowProps> = ({ onToggleRightPanel, onOpenSearch, onBack }) => {
  const [searchParams] = useSearchParamsV6();
  const id = searchParams.get("id") || "";
  const t = searchParams.get("t") || "";
  const roomId = searchParams.get("roomId") || "";

  // Use different hooks based on params:
  // - If id & t exist: use useGetInfoRoom (for contacts)
  // - If only roomId exists: use useJoinAChatRoom directly (for recent chats)
  const {
    roomData: contactRoomData,
    isLoading: isContactRoomLoading,
    isError: isContactRoomError,
  } = useGetInfoRoom(id, t);
  const {
    data: directRoomData,
    isLoading: isDirectRoomLoading,
    isError: isDirectRoomError,
  } = useJoinAChatRoom({
    roomId: roomId,
  });

  // Determine which room data to use
  const finalRoomInfo: RoomInfo | undefined = contactRoomData || directRoomData?.Data;
  const isRoomLoading = isContactRoomLoading || isDirectRoomLoading;
  const isRoomError = isContactRoomError || isDirectRoomError;

  // Create Azure ACS client
  const { data: client, isLoading: isClientLoading } = useClient(finalRoomInfo);

  // Enable realtime messages
  useRealtimeMessage(client?.chatClient as ChatClient, finalRoomInfo?.ThreadId);

  // Get current user ID from room info
  const currentUserId = useMemo(() => {
    return finalRoomInfo?.CUI || "";
  }, [finalRoomInfo?.CUI]);

  // Get current user info to check admin status
  const { data: infoMine } = useInfoMine();

  // Check if current user is admin in this room
  const { data: adminListResponse } = useCheckListUserInRoomAdmin({
    roomId: finalRoomInfo?.RoomId || "",
    userIds: infoMine?.User?.Id ? [infoMine.User.Id] : [],
  });

  const isAdmin = infoMine?.User?.Id ? adminListResponse?.Data?.includes(infoMine.User.Id) : false;

  // Determine if user can send messages
  // - Allow if NOT a channel (C) room
  // - Allow if IS a channel (C) room AND user is admin
  const canSendMessage = useMemo(() => {
    return finalRoomInfo?.RoomType !== "C" || (finalRoomInfo?.RoomType === "C" && isAdmin);
  }, [finalRoomInfo?.RoomType, isAdmin]);

  // Reply state
  const [replyTo, setReplyTo] = useState<ReplyState | null>(null);

  // Ref for MessageList to enable scroll-to-message
  const messageListRef = useRef<MessageListRef>(null);

  const handleCancelReply = () => {
    setReplyTo(null);
  };

  // Handle scroll to pinned message
  const handleScrollToMessage = (messageId: string) => {
    messageListRef.current?.scrollToMessage(messageId);
  };

  // Safari/Android Visibility Fix: Force scroll input into view on window resize (keyboard appear)
  useEffect(() => {
    const isMobile = window.innerWidth <= 768;
    if (!isMobile) return;

    const handleResize = () => {
      // Small delay to let the browser finish the keyboard animation
      setTimeout(() => {
        const inputArea = document.querySelector(`[class*="messageInputContainer"]`);
        if (inputArea) {
          inputArea.scrollIntoView({ behavior: "smooth", block: "end" });
        }
      }, 300);
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [roomId]); // Re-bind on room change to be safe

  // Hiển thị homepage khi chưa chọn phòng chat
  if (!roomId && !id) {
    return (
      <div className={styles.chatWindow}>
        <ChatHomepage />
      </div>
    );
  }

  // Loading state
  if (isRoomLoading || isClientLoading) {
    return (
      <div className={styles.chatWindow}>
        <div className={styles.loadingContainer}>
          <Spin size="large" />
          <p>Đang tải phòng chat...</p>
        </div>
      </div>
    );
  }

  // Error state - Room not found or error fetching room data
  if (isRoomError || (!finalRoomInfo && (roomId || id))) {
    return (
      <div className={styles.chatWindow}>
        <div className={styles.errorContainer}>
          <div className={styles.errorContent}>
            <svg
              width="64"
              height="64"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              style={{ color: "#ff4d4f", marginBottom: "16px" }}
            >
              <circle cx="12" cy="12" r="10" />
              <line x1="12" y1="8" x2="12" y2="12" />
              <line x1="12" y1="16" x2="12.01" y2="16" />
            </svg>
            <h3 style={{ fontSize: "18px", fontWeight: 600, marginBottom: "8px", color: "#262626" }}>
              Không tìm thấy phòng chat
            </h3>
            <p style={{ fontSize: "14px", color: "#8c8c8c", marginBottom: "24px" }}>
              Phòng chat không tồn tại hoặc bạn không có quyền truy cập.
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Determine CSS classes for window
  const activeRoomId = finalRoomInfo?.RoomId || roomId;

  return (
    <div className={styles.chatWindow}>
      {/* Chat Header */}
      <ChatHeader onToggleRightPanel={onToggleRightPanel} onOpenSearch={onOpenSearch} onBack={onBack} />
      {/* Pinned Messages - Below header */}
      <PinnedMessages roomId={activeRoomId} onScrollToMessage={handleScrollToMessage} />
      {/* Message List - Scrollable */}
      <MessageList
        ref={messageListRef}
        chatThreadClient={client?.chatThreadClient}
        threadId={finalRoomInfo?.ThreadId}
        currentUserId={currentUserId}
        actualUserId={infoMine?.User?.Id}
        roomType={finalRoomInfo?.RoomType}
        onReply={setReplyTo}
        isAdmin={isAdmin || false}
      />
      {/* Message Input - Fixed bottom với support image preview & reply */}
      {canSendMessage && <MessageInput roomId={activeRoomId} replyTo={replyTo} onCancelReply={handleCancelReply} />}
    </div>
  );
};

export default ChatWindow;
